import { useState, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  Pencil,
  RefreshCw,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/request/StatusBadge";
import { PodBadge } from "@/components/request/PodBadge";
import { TypeBadge } from "@/components/request/TypeBadge";
import { PriorityBadge } from "@/components/request/PriorityBadge";
import { FileAttachment } from "@/components/request/FileAttachment";
import { EditRequestDialog } from "@/components/request/EditRequestDialog";
import { ResubmitDialog } from "@/components/request/ResubmitDialog";
import {
  TruncatedBody,
  MessageThread,
} from "@/components/request/MessageThread";
import { useAuth } from "@/hooks/useAuth";
import {
  useRequest,
  useRespondToRequest,
  useCancelRequest,
  useResubmitRequest,
} from "@/hooks/useRequests";
import { useThread } from "@/hooks/useThread";
import { useToast } from "@/components/ui/use-toast";
import { formatDate, formatDateTime } from "@/lib/utils";
import { usersApi } from "@/lib/api";
import type { RequestStatus, MentionDetail } from "@/lib/types";

interface UserSuggestion {
  oid: string;
  email: string;
  display_name: string;
}

// Mirrors the backend EDITABLE_STATUSES set in app/api/requests.py
const EDITABLE_STATUSES: ReadonlySet<RequestStatus> = new Set<RequestStatus>([
  "Submitted",
  "InReview",
  "AwaitingInfo",
  "InfoReceived",
]);

// Statuses from which a request can be cancelled
const CANCELLABLE_STATUSES: ReadonlySet<RequestStatus> = new Set<RequestStatus>(
  ["Submitted", "InReview", "AwaitingInfo", "InfoReceived"],
);

export function RespondPage() {
  const { id } = useParams<{ id: string }>();
  const { data: req, isLoading, isError } = useRequest(id ?? "");
  const { mutate: respond, isPending } = useRespondToRequest(id ?? "");
  const { mutate: cancel, isPending: isCancelling } = useCancelRequest(
    id ?? "",
  );
  const { data: messages = [] } = useThread(id ?? "");
  const lastClarificationQ = [...messages]
    .reverse()
    .find((m) => m.message_type === "clarification_question");
  const { toast } = useToast();
  const { isAuthenticated, user, isPM } = useAuth();
  const [responseText, setResponseText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [mentions, setMentions] = useState<MentionDetail[]>([]);
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const responseTextareaRef = useRef<HTMLTextAreaElement>(null);

  if (!id) return <Navigate to="/" replace />;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isError || !req) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold">Request not found</p>
          <p className="text-sm text-muted-foreground mt-1">
            This link may be invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  const isAwaitingInfo = req.status === "AwaitingInfo";
  const isOwnRequest = isAuthenticated && user?.email === req.submitter_email;
  const canEdit = (isOwnRequest || isPM) && EDITABLE_STATUSES.has(req.status);
  const canCancel = isOwnRequest && CANCELLABLE_STATUSES.has(req.status);
  const isFinalStatus = req.status === "Cancelled" || req.status === "Rejected";
  const canResubmit = isOwnRequest && isFinalStatus;
  const canUploadFiles =
    isOwnRequest && !["Rejected", "Closed", "Cancelled"].includes(req.status);
  // Authenticated users came in via /my-requests; anonymous users came from an
  // email link and shouldn't have a "back" target inside the app.
  const backTo = isAuthenticated ? "/my-requests" : null;

  const getMentionContext = (text: string, cursorPos: number) => {
    const beforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");
    if (lastAtIndex === -1) return null;
    const afterAt = beforeCursor.substring(lastAtIndex + 1);
    if (afterAt.includes(" ")) return null;
    return { query: afterAt, atIndex: lastAtIndex };
  };

  const handleResponseTextChange = async (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    setResponseText(newText);
    const context = getMentionContext(newText, cursorPos);
    if (context && context.query.length > 0) {
      setShowSuggestions(true);
      try {
        const users = await usersApi.list(context.query);
        setSuggestions(
          users.filter((u) => !mentions.some((m) => m.oid === u.oid)),
        );
      } catch {
        setSuggestions([]);
      }
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleMentionSelect = (user: UserSuggestion) => {
    const cursorPos = responseTextareaRef.current?.selectionStart || 0;
    const beforeCursor = responseText.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");
    if (lastAtIndex === -1) return;
    const newText = `${responseText.substring(0, lastAtIndex)}@${user.display_name} ${responseText.substring(cursorPos)}`;
    setResponseText(newText);
    setMentions([
      ...mentions,
      { oid: user.oid, name: user.display_name, email: user.email },
    ]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleRespond = () => {
    if (!responseText.trim()) return;
    respond(
      { body: responseText.trim(), mentions: mentions.map((m) => m.oid) },
      {
        onSuccess: () => {
          toast({
            title: "Response submitted",
            description: "The review team has been notified.",
          });
          setResponseText("");
          setMentions([]);
        },
        onError: (err) =>
          toast({
            title: "Failed to submit",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleCancel = () => {
    // Keep dialog open while cancelling
    cancel(undefined, {
      onSuccess: () => {
        // Close dialog only after successful cancellation
        setShowCancelConfirm(false);
        toast({
          title: "Request cancelled",
          description: "Your request has been cancelled successfully.",
        });
      },
      onError: (err) => {
        // Close dialog and show error
        setShowCancelConfirm(false);
        toast({
          title: "Failed to cancel",
          description: err.message,
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 relative">
      {(req.status === "Cancelled" || req.status === "Rejected") && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          <div
            className={`text-9xl font-bold opacity-10 rotate-[-45deg] select-none whitespace-nowrap ${
              req.status === "Cancelled" ? "text-orange-600" : "text-red-600"
            }`}
          >
            {req.status.toUpperCase()}
          </div>
        </div>
      )}
      <div className="mx-auto max-w-2xl px-4 space-y-6">
        {/* Brand + back navigation */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="h-4 w-4 text-primary-foreground"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <span className="font-semibold text-foreground">Blink Relay</span>
            <span className="text-muted-foreground">·</span>
            <span>Request Response</span>
          </div>

          {backTo && (
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
            >
              <Link to={backTo}>
                <ArrowLeft className="h-4 w-4" />
                Back to my requests
              </Link>
            </Button>
          )}
        </div>

        {/* Request summary */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 min-w-0">
                <CardTitle className="text-lg break-words">
                  {req.title}
                </CardTitle>
                {req.reference_id && (
                  <p className="text-xs font-mono text-muted-foreground">
                    {req.reference_id}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditOpen(true)}
                    className="gap-1.5"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                )}
                {canResubmit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setResubmitOpen(true)}
                    className="gap-1.5"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Resubmit
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCancelConfirm(true)}
                    className="gap-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    Cancel Request
                  </Button>
                )}
                <StatusBadge status={req.status} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <TypeBadge type={req.request_type} />
              <PodBadge pod={req.pod} />
              <PriorityBadge priority={req.priority} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Business Problem
              </p>
              <p className="mt-1 text-sm whitespace-pre-wrap break-words break-all">
                {req.business_problem}
              </p>
            </div>
            {req.expected_outcome && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Expected Outcome
                </p>
                <p className="mt-1 text-sm whitespace-pre-wrap break-words break-all">
                  {req.expected_outcome}
                </p>
              </div>
            )}
            <div className="text-xs text-muted-foreground border-t pt-3">
              Submitted {formatDate(req.created_at)} by {req.submitter_name}
            </div>
          </CardContent>
        </Card>

        {/* Awaiting info — action required */}
        {isAwaitingInfo && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
            <CardHeader>
              <CardTitle className="text-base text-amber-800 dark:text-amber-300">
                Action Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lastClarificationQ ? (
                <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-3">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-1">
                    Reviewer asked
                  </p>
                  <div className="text-sm text-blue-900 dark:text-blue-200">
                    <TruncatedBody text={lastClarificationQ.body} />
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    — {lastClarificationQ.author_name} ·{" "}
                    {formatDateTime(lastClarificationQ.created_at)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-amber-700">
                  A reviewer has requested additional information. Please
                  provide your response below.
                </p>
              )}
              <div className="relative">
                <Textarea
                  ref={responseTextareaRef}
                  value={responseText}
                  onChange={handleResponseTextChange}
                  placeholder="Provide the additional information requested… (type @ to mention someone)"
                  rows={5}
                  className="resize-y bg-white dark:bg-gray-900"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 border border-muted-foreground/30 rounded-md bg-white dark:bg-gray-900 shadow-lg z-10">
                    {suggestions.map((s) => (
                      <button
                        key={s.oid}
                        type="button"
                        onClick={() => handleMentionSelect(s)}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-b-0 flex flex-col gap-0.5"
                      >
                        <span className="font-medium">{s.display_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {s.email}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {mentions.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {mentions.map((m) => (
                    <span
                      key={m.oid}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-950 px-2 py-1 text-xs text-blue-700 dark:text-blue-300"
                    >
                      @{m.name}
                      <button
                        type="button"
                        onClick={() =>
                          setMentions(mentions.filter((u) => u.oid !== m.oid))
                        }
                        className="hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Button
                onClick={handleRespond}
                disabled={!responseText.trim() || isPending}
                className="gap-2"
              >
                {isPending ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Submit Response
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Jira ticket banner — shown when a ticket exists (Approved and beyond) */}
        {!isAwaitingInfo && req.jira_ticket_key && (
          <div className="rounded-lg border-2 border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 px-4 py-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              Jira Implementation Ticket
            </p>
            <a
              href={req.jira_ticket_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-md border border-blue-300 bg-white dark:bg-gray-900 dark:border-blue-800 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors group"
            >
              <span className="text-xl font-bold text-blue-700 tracking-tight">
                {req.jira_ticket_key}
              </span>
              <ExternalLink className="h-5 w-5 text-blue-500 group-hover:text-blue-700" />
            </a>
            <p className="text-xs text-blue-700">
              Your request has been approved — click the ticket above to track
              implementation progress in Jira.
            </p>
          </div>
        )}

        {/* Status message for non-awaiting states */}
        {!isAwaitingInfo && !req.jira_ticket_key && (
          <div className="rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
            {req.status === "InfoReceived"
              ? "Your response has been received. The review team will follow up shortly."
              : req.status === "Approved"
                ? "This request has been approved. A Jira ticket will be created shortly."
                : req.status === "Rejected"
                  ? "This request has been closed. Contact your PM if you have questions."
                  : "No action is currently required. You will be notified of any updates by email."}
          </div>
        )}

        {/* Conversation thread */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Conversation</CardTitle>
          </CardHeader>
          <CardContent>
            <MessageThread
              requestId={req.id}
              internalOnly={false}
              disabled={isFinalStatus}
            />
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attachments</CardTitle>
          </CardHeader>
          <CardContent>
            <FileAttachment requestId={req.id} canUpload={canUploadFiles} />
          </CardContent>
        </Card>
      </div>

      {/* Edit dialog — mounted only when the user is allowed to open it */}
      {canEdit && (
        <EditRequestDialog
          request={req}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}

      {/* Resubmit dialog — for cancelled/rejected own requests */}
      {canResubmit && (
        <ResubmitDialog
          request={req}
          open={resubmitOpen}
          onOpenChange={setResubmitOpen}
        />
      )}

      {/* Cancel confirmation dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
                  {isCancelling ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <CardTitle className="text-red-900">
                  {isCancelling ? "Cancelling Request..." : "Cancel Request?"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {isCancelling
                  ? "Please wait while we cancel your request..."
                  : "Are you sure you want to cancel this request? This action cannot be undone, and the review team will be notified."}
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                >
                  {isCancelling ? "Please wait..." : "Keep Request"}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                      Cancelling…
                    </>
                  ) : (
                    "Cancel Request"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
